'use client';

import { useListLinks, useUpdateLinkOptions, useGetBotData } from '@/hooks/useBot';
import { pageCTADefaultValues } from '@/constants/bot';
import { ICTAOption, IPageCTAForm, IUpdateLinkOptionBody } from '@/types/page-cta';
import { handleShowError } from '@/utils';
import { pageCtaSchema } from '@/validations/page-cta';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  MousePointerClick, ListTree, MessageSquareText, Globe, Palette, RotateCcw, Sparkles,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { FC, useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ChromePicker } from 'react-color';
import { updateBot } from '@/services/bots';
import ShadCnFormInput from '@/components/FormComponents/ShadCnFormInput';
import AllURLs from './AllURLs';
import AISuggestionsPanel from './AISuggestionsPanel';
import CTAOptionItem from './CTAOptionItem';
import MultipleURLSearch from './MultipleURLSearch';
import Preview from './Preview';
import PageMessagesManager from './PageMessagesManager';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  StickyActionBar, SectionCard, SubTabBar, InfoBanner, PreviewTabLayout, type SubTabItem,
} from '../shared';

type PopupTargetMode = 'sitewide' | 'specific';

interface IPageCTAsProps {
  token?: string;
  primaryColor: string;
}

enum TAB {
  CTA_SETTINGS = 'CTA_SETTINGS',
  ALL_URLS = 'ALL_URLS',
  WELCOME_PROMPTS = 'WELCOME_PROMPTS',
}

const TABS: readonly SubTabItem<TAB>[] = [
  { value: TAB.CTA_SETTINGS, label: 'CTA Settings', icon: MousePointerClick },
  { value: TAB.ALL_URLS, label: 'All Pages', icon: ListTree },
  { value: TAB.WELCOME_PROMPTS, label: 'Welcome & Prompts', icon: MessageSquareText },
];

const PageCTAs: FC<IPageCTAsProps> = ({ primaryColor, token }) => {
  const [activeTab, setActiveTab] = useState<TAB>(TAB.CTA_SETTINGS);
  const [popupColor, setPopupColor] = useState<string>('');
  const [popupTextColor, setPopupTextColor] = useState<string>('');
  const [pickerOpen, setPickerOpen] = useState<'bg' | 'text' | null>(null);
  const [pickerPos, setPickerPos] = useState<{ top: number; left: number } | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const pickerBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // 'sitewide': Headline/Greeting edit customizationdata.popupHeadline/popupGreeting
  // directly (updateBot) and apply everywhere. 'specific': they edit a per-page
  // vendor_links override (updateLinkOptions) and require >=1 page selected.
  // An empty "Where it appears" selection is NOT an error state on its own — it's
  // only invalid while in 'specific' mode. Defaults to 'sitewide' until the bot's
  // existing per-page links are known (see hydration effect below).
  const [popupMode, setPopupMode] = useState<PopupTargetMode>('sitewide');
  const [modeHydrated, setModeHydrated] = useState(false);

  // Derive bot display name, icon and existing popup colors from customizationdata.
  const { data: botData, refetch: refetchBotData } = useGetBotData(token || '');
  const customization = botData?.data?.customizationdata as Record<string, any> | undefined;
  const aiSuggestionsEnabled = botData?.data?.ai_suggestions_enabled === true;
  const botIconUrl = customization?.botIcon?.uploadedUri || customization?.launchIcon?.uploadedUri || '';
  // Precedence must mirror the server's engage payload exactly
  // (chatbotController.handlePageVisit: `customization.name || customization.botName`).
  // This was inverted here, so a vendor that sets both saw their bot name in the
  // preview and their company name on the live popup (etherfax: "AI Navigator"
  // vs "ETHERFAX", reported 2026-07-31).
  const botName = customization?.name || customization?.botName || 'Assistant';
  // When a page has no CTA override configured, the live popup falls back to the
  // global welcomeMessage. The preview used to show hardcoded placeholder copy
  // ("👋 Hi there!" / "How can we assist you today?") that exists nowhere in the
  // vendor's config, so it could never match the site no matter what they set.
  const globalWelcome = typeof customization?.welcomeMessage === 'string'
    ? customization.welcomeMessage.trim()
    : '';
  const sitewideHeadline = typeof customization?.popupHeadline === 'string' ? customization.popupHeadline : '';
  // popupGreeting is its OWN field, distinct from welcomeMessage (the in-chat welcome
  // message, edited in the Appearance/Customize tab — a different surface, typically much
  // longer copy). They used to share a field, so setting a short pre-chat popup greeting
  // silently overwrote the vendor's in-chat welcome message and vice versa — reported live
  // by etherfax (2026-08-01). Mirrors chatbotController.handlePageVisit's precedence
  // exactly: once a bot has engaged with the Site-wide editor at all (either key present,
  // even as an empty string), popupGreeting is used as-is — no silent re-fill from
  // welcomeMessage, since a vendor may deliberately want a headline with no second line.
  const hasSitewidePopupConfig = customization?.popupHeadline !== undefined
    || customization?.popupGreeting !== undefined;
  const sitewideGreeting = hasSitewidePopupConfig
    ? (typeof customization?.popupGreeting === 'string' ? customization.popupGreeting : '')
    : globalWelcome;

  // Hydrate popup colors when bot data loads.
  useEffect(() => {
    if (customization?.popupColor !== undefined) setPopupColor(customization.popupColor || '');
    if (customization?.popupTextColor !== undefined) setPopupTextColor(customization.popupTextColor || '');
  }, [botData]); // eslint-disable-line react-hooks/exhaustive-deps

  const form = useForm<IPageCTAForm>({
    defaultValues: pageCTADefaultValues as any,
    resolver: yupResolver(pageCtaSchema) as any,
  });

  const { mutateAsync: updateLinkOptionsAsync } = useUpdateLinkOptions();
  const {
    data,
    isLoading: isLinksLoading,
    queryParams,
    setQueryParams,
  } = useListLinks(token || '');

  // This panel doesn't persist/hydrate a "which page is currently being edited"
  // selection across visits at all today (searching and re-selecting a page is
  // required every time, even for pages with existing saved content — a known,
  // pre-existing, separate gap, not introduced here). There is therefore no
  // reliable signal to hydrate an initial toggle position FROM; default to the
  // deterministic, predictable choice and let the vendor switch explicitly.
  useEffect(() => {
    if (modeHydrated || !botData) return;
    setModeHydrated(true);
  }, [modeHydrated, botData]);

  // Keep the Headline/Greeting inputs showing the values that actually apply for
  // the current mode. Site-wide: the bot-level customizationdata fields (real saved
  // values — NOT pageCTADefaultValues' hardcoded placeholder copy, which is only a
  // display placeholder for the per-page flow's blank state). Specific: cleared back
  // to blank until a page is picked (matches existing behavior for the per-page flow
  // — there's no "current page" to show content for yet).
  // Also called directly from handleReset so Reset restores the SAVED site-wide
  // content, not form.reset()'s baseline placeholder text — Save afterward would
  // otherwise silently overwrite customizationdata.popupGreeting (or, before this
  // field existed, welcomeMessage — the in-chat default welcome bubble, a completely
  // different surface) with placeholder copy the vendor never wrote.
  const syncContentFieldsToMode = (mode: PopupTargetMode) => {
    if (mode === 'sitewide') {
      form.setValue('popup_headline', sitewideHeadline);
      form.setValue('popup_greeting', sitewideGreeting);
      form.setValue('urls', []);
    } else if (!form.getValues('urls')?.length) {
      form.setValue('popup_headline', '');
      form.setValue('popup_greeting', '');
    }
    // No page is being edited in either branch, so there is no page whose saved copy
    // these mirror. Leaving a previous page's values behind would make the blank-clear
    // guard fire against content that is no longer on screen.
    if (mode === 'sitewide' || !form.getValues('urls')?.length) {
      form.setValue('stored_popup_headline', '');
      form.setValue('stored_popup_greeting', '');
    }
  };

  useEffect(() => {
    if (!modeHydrated) return;
    syncContentFieldsToMode(popupMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupMode, modeHydrated]);

  useEffect(() => {
    handleValidateOption(form.watch('option_a'), form.watch('option_b'), form.watch('option_c'));
  }, [form.watch('option_a.type'), form.watch('option_b.type'), form.watch('option_c.type')]); // eslint-disable-line react-hooks/exhaustive-deps

  // A slot only has to be COMPLETE once the vendor has picked an action for it; an
  // untouched slot is simply a hidden button, exactly as this section's subtitle says.
  //
  // All three untouched therefore means "a popup with no action buttons" — and that was
  // the one state this used to make unreachable: with no type anywhere it flipped every
  // slot to required, so form.trigger() could never pass and handleSave fell through to
  // "Please complete the highlighted CTA fields". Clearing the buttons was impossible,
  // which is what Pharos AI hit on their /pricing popup (2026-08-25).
  const handleValidateOption = (optionA: ICTAOption, optionB: ICTAOption, optionC: ICTAOption) => {
    [optionA, optionB, optionC].forEach((option, index) => {
      const fieldNames = ['option_a', 'option_b', 'option_c'] as const;
      if (option?.type) {
        form.setValue(`${fieldNames[index]}.isRequired` as const, true);
      } else {
        form.setValue(`${fieldNames[index]}.isRequired` as const, false);
        form.trigger(fieldNames[index]);
      }
    });
  };

  const effectivePopupColor = popupColor || primaryColor;
  const effectivePopupTextColor = popupTextColor || '#ffffff';
  // Whether any custom popup color is currently set (drives the banner + Reset all).
  const hasCustomColors = !!popupColor || !!popupTextColor;
  // Whether the in-memory colors differ from what's saved (drives whether Save
  // needs to write customizationdata at all).
  const hasColorChanges = popupColor !== (customization?.popupColor || '')
    || popupTextColor !== (customization?.popupTextColor || '');

  const buildLinkBody = (formData: IPageCTAForm): IUpdateLinkOptionBody => {
    const { option_a, option_b, option_c, popup_greeting, popup_headline, urls, url } = formData || {};
    return {
      options: {
        option_a: option_a?.type ? option_a : null,
        option_b: option_b?.type ? option_b : null,
        option_c: option_c?.type ? option_c : null,
      },
      urls: urls?.length ? urls : [url],
      video_link: null,
      popup_greeting,
      popup_headline,
    };
  };

  // One Save persists up to three surfaces, saved independently:
  //  - Popup colors → bot-wide customizationdata (updateBot). Saved whenever
  //    changed, REGARDLESS of mode or CTA form validity.
  //  - Site-wide mode: Headline/Greeting → customizationdata.popupHeadline /
  //    popupGreeting (updateBot). Applies everywhere; no page target needed.
  //    Deliberately its OWN field, not welcomeMessage (the in-chat welcome
  //    message, a different surface edited in the Appearance/Customize tab) —
  //    they used to share a field, so editing one silently overwrote the other.
  //    CTA buttons have no site-wide storage today, so they're not editable in
  //    this mode (see the note rendered in that section) and are not saved here.
  //  - Specific-pages mode: Headline/Greeting + CTA buttons → per-URL vendor_links
  //    (updateLinkOptions), same as before. Saved only when >=1 page is targeted
  //    and the form validates.
  const handleSave = async () => {
    setIsSaving(true);
    try {
      let colorSaved = false;
      let contentSaved = false;
      let ctaFieldsInvalid = false;

      if (hasColorChanges && token) {
        // updateBot swallows its own errors (returns null on any failure, including
        // non-2xx responses) rather than throwing — check the result explicitly, or
        // a failed save (e.g. a 403 from the plan-gated endpoint) looks identical to
        // a successful one and still shows a green "saved" toast.
        const res = await updateBot({ popupColor, popupTextColor }, token);
        if (!res) throw new Error('Failed to save popup colors.');
        colorSaved = true;
      }

      if (popupMode === 'sitewide') {
        if (token) {
          const res = await updateBot(
            {
              popupHeadline: form.getValues('popup_headline') || '',
              popupGreeting: form.getValues('popup_greeting') || '',
            },
            token,
          );
          if (!res) throw new Error('Failed to save popup content.');
          contentSaved = true;
        }
      } else {
        const urls = form.getValues('urls');
        const hasUrlTarget = Array.isArray(urls) && urls.length > 0;
        if (hasUrlTarget) {
          const valid = await form.trigger();
          if (valid) {
            await updateLinkOptionsAsync(buildLinkBody(form.getValues()));
            contentSaved = true;
          } else {
            ctaFieldsInvalid = true;
          }
        } else if (!colorSaved) {
          toast.error('Select at least one page, or change a color, to save.');
          return;
        }
      }

      if (contentSaved) {
        refetchBotData();
        toast.success('Pre-chat popup saved.');
      } else if (colorSaved) {
        refetchBotData();
        toast.success(
          ctaFieldsInvalid
            ? 'Popup colors saved. Fix the highlighted fields to save the rest.'
            : 'Popup colors saved.',
        );
      } else {
        toast.error('Please complete the highlighted CTA fields.');
      }
    } catch (e) {
      handleShowError(e);
    } finally {
      setIsSaving(false);
    }
  };

  // Reset discards unsaved edits: form back to its baseline, colors back to saved.
  // form.reset() alone would leave Headline/Greeting on pageCTADefaultValues'
  // hardcoded placeholder text in Site-wide mode — re-sync immediately after so
  // Reset restores the real saved site-wide content instead.
  const handleReset = () => {
    form.reset();
    syncContentFieldsToMode(popupMode);
    setPopupColor(customization?.popupColor || '');
    setPopupTextColor(customization?.popupTextColor || '');
    setPickerOpen(null);
  };

  const openPicker = (key: 'bg' | 'text') => {
    const rect = pickerBtnRefs.current[key]?.getBoundingClientRect();
    if (rect) {
      const pickerH = 300;
      const above = rect.bottom + pickerH > window.innerHeight - 20;
      setPickerPos({
        left: Math.min(rect.left, window.innerWidth - 240),
        top: above ? rect.top - pickerH - 4 : rect.bottom + 4,
      });
    }
    setPickerOpen(key);
  };

  // Colour swatch with an "Auto" badge (when following Primary / default white) + reset.
  const renderSwatch = (
    key: 'bg' | 'text',
    label: string,
    effective: string,
    raw: string,
    onReset: () => void,
    autoLabel: string,
  ) => {
    const isAuto = !raw;
    return (
      <div className="flex flex-col gap-1">
        {/* Fixed-height header; the Auto reset is absolutely positioned so it never
            changes the row height or shifts the swatch below it. */}
        <div className="relative flex h-4 items-center">
          <p className="text-xs font-semibold text-gray-700">{label}</p>
          {!isAuto && (
            <button
              type="button"
              onClick={onReset}
              className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-0.5 p-0 leading-none text-[10px] text-gray-400 hover:text-gray-600"
            >
              <RotateCcw className="h-2.5 w-2.5" /> Auto
            </button>
          )}
        </div>
        <button
          ref={(el) => { pickerBtnRefs.current[key] = el; }}
          type="button"
          onClick={() => openPicker(key)}
          className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-left hover:border-gray-300 transition-colors"
        >
          <span className="h-5 w-5 shrink-0 rounded-md border border-gray-200 shadow-sm" style={{ backgroundColor: effective }} />
          {isAuto
            ? <span className="text-[10px] font-medium text-gray-400 italic">{autoLabel}</span>
            : <span className="text-xs text-gray-600 font-mono truncate">{raw}</span>}
        </button>
      </div>
    );
  };

  return (
    <FormProvider {...form}>
      <div className="flex-1 min-h-0 flex flex-col">
        <SubTabBar tabs={TABS} value={activeTab} onChange={setActiveTab} />

        {/* CTA Settings: config column + live preview, matching the Appearance tab */}
        {activeTab === TAB.CTA_SETTINGS && (
          <PreviewTabLayout
            preview={(
              <div className="h-full bg-slate-50 flex flex-col items-center justify-center gap-4 p-6 overflow-y-auto">
                <p className="text-sm font-semibold text-gray-700">Live preview</p>
                <Preview
                  popupGreeting={form.watch('popup_greeting')}
                  popupHeadline={form.watch('popup_headline')}
                  fallbackMessage={globalWelcome}
                  // Any Save made while in Site-wide mode sends both popupHeadline and
                  // popupGreeting (even as empty strings), which permanently makes
                  // hasSitewidePopupConfig true for this bot going forward — so the
                  // no-fallback behavior applies the moment the vendor is IN this mode,
                  // not only after they've already saved once. Preview must reflect what
                  // Save will actually produce, not just the currently-persisted state.
                  forceNoFallback={popupMode === 'sitewide'}
                  optionALabel={form.watch('option_a.label')}
                  optionBLabel={form.watch('option_b.label')}
                  optionCLabel={form.watch('option_c.label')}
                  primaryColor={effectivePopupColor}
                  textColor={effectivePopupTextColor}
                  botIconUrl={botIconUrl}
                  botName={botName}
                />
                <p className="text-xs text-gray-400 text-center max-w-[280px] leading-relaxed">
                  How the popup appears on your site before a visitor opens the chat.
                </p>
              </div>
            )}
            footer={(
              <StickyActionBar
                onReset={handleReset}
                onSave={() => { void handleSave(); }}
                saving={isSaving}
                // Also gated on modeHydrated: saving before the bot's real site-wide
                // content has loaded would write the form's still-placeholder Headline/
                // Greeting values over the real saved ones.
                saveDisabled={isSaving || !modeHydrated}
                resetDisabled={isSaving || !modeHydrated}
                saveLabel="Save"
              />
            )}
          >
            <div className="space-y-6">
              <InfoBanner variant="info">
                This configures the <strong>pre-chat popup</strong> — the floating card shown on your site
                <strong> before</strong> a visitor opens the chat. It is separate from the in-chat welcome
                buttons in the Appearance tab.
              </InfoBanner>

              {/* Targeting */}
              <SectionCard
                icon={Globe}
                title="Where it appears"
                subtitle="Choose whether this popup content applies everywhere or only to specific pages."
              >
                <RadioGroup
                  value={popupMode}
                  onValueChange={(v) => setPopupMode(v as PopupTargetMode)}
                  className="flex flex-col gap-3 mb-1"
                >
                  <label htmlFor="popup-mode-sitewide" className="flex cursor-pointer items-start gap-2">
                    <RadioGroupItem value="sitewide" id="popup-mode-sitewide" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Site-wide</p>
                      <p className="text-xs text-gray-500">Applies to every page on your site.</p>
                    </div>
                  </label>
                  <label htmlFor="popup-mode-specific" className="flex cursor-pointer items-start gap-2">
                    <RadioGroupItem value="specific" id="popup-mode-specific" className="mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-800">Specific pages</p>
                      <p className="text-xs text-gray-500">Overrides the site-wide popup only on the page(s) you pick below.</p>
                    </div>
                  </label>
                </RadioGroup>
                {popupMode === 'specific' && <MultipleURLSearch />}
              </SectionCard>

              {/* Content */}
              <SectionCard
                icon={MessageSquareText}
                title="Popup content"
                subtitle={
                  popupMode === 'sitewide'
                    ? 'The headline and greeting visitors read in the popup card, on every page.'
                    : 'The headline and greeting visitors read in the popup card, on the selected page(s).'
                }
              >
                {popupMode === 'specific' && !form.watch('urls')?.length && (
                  <InfoBanner variant="warning" title="Select a page above first">
                    In "Specific pages" mode, headline and greeting only apply to the page(s) selected in
                    &quot;Where it appears&quot;. Without a page selected, this content won&apos;t save — switch to
                    &quot;Site-wide&quot; above if you want it to apply everywhere instead.
                  </InfoBanner>
                )}
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">Headline</p>
                  <ShadCnFormInput name="popup_headline" placeholder="e.g. Welcome! How can we assist you today?" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900 mb-1.5">Greeting</p>
                  <ShadCnFormInput name="popup_greeting" placeholder="e.g. We're here to help — choose an option below." />
                </div>
                {token ? <AISuggestionsPanel chatToken={token} /> : null}
              </SectionCard>

              {/* CTA buttons */}
              <SectionCard
                icon={MousePointerClick}
                title="Call-to-action buttons"
                subtitle="Up to three quick actions shown in the popup. Leave a slot empty to hide it."
              >
                {popupMode === 'sitewide' ? (
                  <InfoBanner variant="info">
                    CTA buttons are configured per-page today. Switch to &quot;Specific pages&quot; above and select
                    a page to set custom buttons for it — otherwise the default buttons (Schedule a Demo / Chat
                    with Our Team) are shown everywhere.
                  </InfoBanner>
                ) : (
                  <div className="flex flex-col gap-4">
                    <CTAOptionItem name="option_a" />
                    <CTAOptionItem name="option_b" />
                    <CTAOptionItem name="option_c" />
                  </div>
                )}
              </SectionCard>

              {/* Colors */}
              <SectionCard
                icon={Palette}
                title="Popup colors"
                subtitle="Background colors the card, buttons & input accent (Auto follows Primary). Text colors the greeting, name & button labels."
              >
                {hasCustomColors && (
                  <div className="flex items-center justify-between rounded-lg bg-amber-50 border border-amber-200 px-3 py-2">
                    <div className="flex items-center gap-2 text-xs text-amber-700">
                      <Sparkles className="h-3.5 w-3.5 shrink-0" />
                      Custom popup colors set. Reset to follow Primary / default.
                    </div>
                    <button
                      type="button"
                      onClick={() => { setPopupColor(''); setPopupTextColor(''); }}
                      className="ml-3 shrink-0 flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                    >
                      <RotateCcw className="h-3 w-3" /> Reset all
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 sm:max-w-sm">
                  {renderSwatch('bg', 'Background', effectivePopupColor, popupColor, () => setPopupColor(''), 'Auto (Primary)')}
                  {renderSwatch('text', 'Text', effectivePopupTextColor, popupTextColor, () => setPopupTextColor(''), 'Auto (White)')}
                </div>
              </SectionCard>
            </div>

            {/* Colour picker portal — fixed position, never clipped by the scroll container */}
            {pickerOpen && pickerPos && typeof document !== 'undefined' && createPortal(
              <>
                <div className="fixed inset-0 z-[999]" onClick={() => setPickerOpen(null)} />
                <div
                  className="fixed z-[1000] shadow-2xl rounded-xl overflow-hidden"
                  style={{ top: pickerPos.top, left: pickerPos.left }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ChromePicker
                    color={pickerOpen === 'text' ? effectivePopupTextColor : effectivePopupColor}
                    onChange={(c) => (pickerOpen === 'text' ? setPopupTextColor(c.hex) : setPopupColor(c.hex))}
                    disableAlpha
                  />
                </div>
              </>,
              document.body,
            )}
          </PreviewTabLayout>
        )}

        {/* All Pages tab */}
        {activeTab === TAB.ALL_URLS && (
          <div className="flex-1 min-h-0 overflow-y-auto p-6">
            <SectionCard
              icon={ListTree}
              title="All Pages"
              subtitle="View all your pages and their attached CTA configurations."
            >
              <AllURLs
                allURLData={data}
                isLoading={isLinksLoading}
                queryParams={queryParams}
                setQueryParams={setQueryParams}
              />
            </SectionCard>
          </div>
        )}

        {/* Welcome & Prompts tab */}
        {activeTab === TAB.WELCOME_PROMPTS && (
          <div className="flex-1 min-h-0 overflow-hidden">
            <PageMessagesManager
              token={token || ''}
              aiSuggestionsEnabled={aiSuggestionsEnabled}
            />
          </div>
        )}
      </div>
    </FormProvider>
  );
};

export default PageCTAs;
